"use client";

import { useEffect, type RefObject } from "react";

/*
 * Schwebende Auswahllisten (Fehler „Auswahlliste im Modal abgeschnitten“, 13.10.2026).
 *
 * Ursache: Die Listen der erweiterten Auswahl (Preline `advanced-select`) und des Trefferfelds hängen `absolute` am Feld.
 * Im Modal sitzt das Feld im scrollenden Körper (`overflow-y-auto`) unter der Hülle mit `overflow-hidden`; eine
 * `absolute` Liste wird dort abgeschnitten und verlängert den Scrollbereich, statt über dem Modal zu stehen.
 *
 * Lösung: Die offene Liste wird zur Laufzeit `position: fixed` an die Maße des Auslösers gelegt. Eine `fixed`
 * Fläche gehört zum Fenster und wird von `overflow` der Vorfahren nicht beschnitten (im Modal gibt es keinen Vorfahren mit
 * `transform`/`filter`). Die Liste bleibt im DOM des Feldes: Fokusfalle und Esc-Vorrang des Modals (modal-verhalten.ts),
 * Tab-Reihenfolge, `aria`-Bezüge und die Stapelebene des Modals (z-80/z-82, Tour z-90) gelten unverändert. Die Koordinaten
 * setzt dieser Baustein zur Laufzeit am Element (wie `dropdown-menue.tsx`); sie sind keine Gestaltung.
 * Preline `dropdownScope: "window"` trägt hier nicht: es braucht das Fremdpaket Floating UI als `window.FloatingUIDOM`
 * (nicht im Repo) und hängt die Liste an den `body`, also aus dem Dialog heraus (Fokusfalle, Esc, aria-modal).
 */
const ABSTAND = 8;
const MIN_HOEHE = 120;

const GESETZT = ["position", "left", "top", "bottom", "width", "margin", "maxHeight"] as const;

/** Eingaben der Platzierung als reine Zahlen (Pixel im Fenster); für Tests ohne DOM. */
export interface PlatzierungEingabe {
  anker: { top: number; bottom: number };
  fensterHoehe: number;
  /** Natürliche Höhe der Liste ohne Begrenzung. */
  natuerlich: number;
  /** Obere Grenze (Fenster 0 oder Unterkante des Modal-Kopfs); `null` = Fensterrand. */
  grenzeOben: number | null;
  /** Untere Grenze (Oberkante der Modal-Fußzeile); `null` = Fensterrand. */
  grenzeUnten: number | null;
}

export interface Platzierung {
  nachOben: boolean;
  /** `null` = keine Begrenzung nötig. */
  maxHoehe: number | null;
}

/**
 * Entscheidet Richtung und Höhe. Fenstergrenzen wie bisher; im Modal begrenzen Kopf und Fußzeile (W130 a): die Liste endet
 * vor der Fußzeile, wird dort kürzer und scrollt. Die Mindesthöhe (drei Einträge) gilt nur ohne Modal-Grenze; im Modal
 * gewinnt der Schutz der Fußzeile, die Liste nimmt dann die größere Seite.
 */
export function berechnePlatzierung(e: PlatzierungEingabe): Platzierung {
  const imModal = e.grenzeOben !== null || e.grenzeUnten !== null;
  const unten = Math.min(e.fensterHoehe, e.grenzeUnten ?? e.fensterHoehe);
  const oben = Math.max(0, e.grenzeOben ?? 0);
  const platzUnten = Math.max(0, unten - e.anker.bottom - ABSTAND);
  const platzOben = Math.max(0, e.anker.top - ABSTAND - oben);
  const nachOben = e.natuerlich > platzUnten && platzOben > platzUnten;
  const verfuegbar = nachOben ? platzOben : platzUnten;
  const platz = imModal ? verfuegbar : Math.max(MIN_HOEHE, verfuegbar);
  return { nachOben, maxHoehe: e.natuerlich > platz ? platz : null };
}

function ueberlappt(a: DOMRect, links: number, rechts: number): boolean {
  return a.left < rechts && a.right > links;
}

/**
 * Grenzen aus dem umgebenden Modal: `[role="dialog"]` mit Kopf (`data-modal-kopf`) und Fußzeile (`data-modal-fuss`). Die
 * Fußzeile zählt nur, wenn sie waagerecht unter der Liste liegt (Detail-Modal: Composer nur in der Hauptspalte).
 */
function modalGrenzen(anker: HTMLElement, links: number, rechts: number): { oben: number | null; unten: number | null } {
  const dialog = anker.closest<HTMLElement>('[role="dialog"]');
  if (!dialog) return { oben: null, unten: null };
  const kopf = dialog.querySelector<HTMLElement>("[data-modal-kopf]");
  const fuss = dialog.querySelector<HTMLElement>("[data-modal-fuss]");
  const fussR = fuss?.getBoundingClientRect();
  return {
    oben: kopf ? kopf.getBoundingClientRect().bottom : null,
    unten: fussR && ueberlappt(fussR, links, rechts) ? fussR.top : null,
  };
}

/** Legt die geöffnete Liste fest ans Fenster, unter (oder bei zu wenig Platz über) den Anker; im Modal innerhalb von Kopf und Fußzeile. */
export function platziereSchwebend(liste: HTMLElement, anker: HTMLElement): void {
  const r = anker.getBoundingClientRect();
  const s = liste.style;
  s.position = "fixed";
  s.margin = "0";
  s.maxHeight = "";
  s.left = `${r.left}px`;
  s.width = `${r.width}px`;
  const grenzen = modalGrenzen(anker, r.left, r.left + r.width);
  const { nachOben, maxHoehe } = berechnePlatzierung({
    anker: { top: r.top, bottom: r.bottom },
    fensterHoehe: window.innerHeight,
    natuerlich: liste.offsetHeight,
    grenzeOben: grenzen.oben,
    grenzeUnten: grenzen.unten,
  });
  if (maxHoehe !== null) s.maxHeight = `${maxHoehe}px`;
  if (nachOben) {
    // Am unteren Rand verankert: schrumpft die Liste (Suche), bleibt sie am Auslöser.
    s.top = "auto";
    s.bottom = `${window.innerHeight - r.top + ABSTAND}px`;
  } else {
    s.bottom = "auto";
    s.top = `${r.bottom + ABSTAND}px`;
  }
}

/** Nimmt die Laufzeit-Maße zurück (Liste geschlossen). */
export function loeseSchwebend(liste: HTMLElement): void {
  for (const eigenschaft of GESETZT) liste.style[eigenschaft] = "";
}

/** Hält die Platzierung bei Scrollen (auch im Modal-Körper) und Größenänderung aktuell; gibt die Abmeldung zurück. */
export function haltePlatziert(liste: HTMLElement, anker: HTMLElement): () => void {
  const neu = () => platziereSchwebend(liste, anker);
  window.addEventListener("scroll", neu, true);
  window.addEventListener("resize", neu);
  return () => {
    window.removeEventListener("scroll", neu, true);
    window.removeEventListener("resize", neu);
  };
}

interface HsInstanz {
  el: HTMLElement;
  dropdown: HTMLElement;
  toggle?: HTMLElement | null;
  wrapper?: HTMLElement | null;
  mode?: string;
}

/**
 * Bindet die Liste einer Preline-Auswahl (`HSSelect`, gefunden über die Id des nativen Feldes) an die Schwebe-Platzierung:
 * Öffnen (Klasse `hidden` fällt) platziert, Schließen löst; Scrollen/Größenänderung führen nach. Gibt die Abmeldung zurück.
 */
export function binde(nativId: string): () => void {
  let abmelden: (() => void) | null = null;
  let abbrechen = false;
  let versuche = 0;

  const verbinde = (): boolean => {
    const sammlung = (window as unknown as { $hsSelectCollection?: { element: HsInstanz }[] }).$hsSelectCollection;
    const instanz = sammlung?.find((e) => e.element.el?.id === nativId)?.element;
    if (!instanz?.dropdown) return false;
    const liste = instanz.dropdown;
    const anker = (instanz.mode === "tags" ? instanz.wrapper : instanz.toggle) ?? instanz.wrapper;
    if (!anker) return false;
    let halten: (() => void) | null = null;
    // Esc-Vorrang (W130 a): solange die Liste offen ist, markiert sie sich und den Auslöser; das Modal lässt Esc dann der
    // Liste (modal-verhalten.ts), Preline schließt sie über seinen Tastatur-Beobachter, die Marke fällt mit dem Schließen.
    const markiere = (offen: boolean) => {
      for (const el of [liste, anker]) {
        if (offen) el.setAttribute("data-esc-eigen", "offen");
        else el.removeAttribute("data-esc-eigen");
      }
    };
    const abgleich = () => {
      const offen = !liste.classList.contains("hidden");
      markiere(offen);
      if (offen) {
        platziereSchwebend(liste, anker);
        halten ??= haltePlatziert(liste, anker);
      } else {
        halten?.();
        halten = null;
        loeseSchwebend(liste);
      }
    };
    const beobachter = new MutationObserver(abgleich);
    beobachter.observe(liste, { attributes: true, attributeFilter: ["class"] });
    abgleich();
    abmelden = () => {
      beobachter.disconnect();
      halten?.();
      markiere(false);
      loeseSchwebend(liste);
    };
    return true;
  };

  // Das Plugin legt die Instanz synchron an; der Wiederholversuch fängt eine spätere Anlage ab.
  const versuche_ = () => {
    if (abbrechen || verbinde()) return;
    if (++versuche < 20) requestAnimationFrame(versuche_);
  };
  versuche_();
  return () => {
    abbrechen = true;
    abmelden?.();
  };
}

/** Trefferfeld und ähnliche React-Listen: Liste und Anker per Ref; platziert, solange `offen` und bei jeder Inhaltsänderung. */
export function useSchwebendeListe(
  offen: boolean,
  liste: RefObject<HTMLElement | null>,
  anker: RefObject<HTMLElement | null>,
  inhaltsschluessel: unknown,
): void {
  useEffect(() => {
    const l = liste.current;
    const a = anker.current;
    if (!offen || !l || !a) return;
    platziereSchwebend(l, a);
    return haltePlatziert(l, a);
  }, [offen, liste, anker, inhaltsschluessel]);
}
